/* eslint-disable @typescript-eslint/restrict-plus-operands */
import React, { useState } from 'react';
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Legend,
  Sector,
} from 'recharts';
import { Typography, Box, Button } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { ThemePalette } from 'mui.theme';
import { getTireName, getDynamicAssetTireInfo } from 'views/TirePressure/util';
import { handleFocusRemoveSelectOutline } from 'views/Settings/ExternalUsers/Squares/CostAnalysis/Assets/Helper/pieChart.Helper';
import { filterLastDaysActive } from '../utils';
import type { MakeModelDetails } from 'types/make-model.types';
import type {
  TirePattern,
  TirePatternPosition,
} from 'types/tire-pattern.types';
import { useTranslation } from 'react-i18next';
import { CLIENT_THEME, clientThemePalette } from 'utils/clientTheme';

interface Props {
  enableDarkTheme: boolean;
  data: any[];
  makeModelDetails: MakeModelDetails[];
  tirePatterns: {
    tirePatterns: TirePattern[];
    tirePatternPositions: TirePatternPosition[];
  };
  customerId: string;
}

export default function TpmsInfo({
  enableDarkTheme,
  data,
  makeModelDetails,
  tirePatterns,
  customerId,
}: Props) {
  const { t } = useTranslation();
  const [activeIndices, setActiveIndices] = useState<Record<number, boolean>>({
    0: true,
  });
  const palette = enableDarkTheme
    ? clientThemePalette[CLIENT_THEME].dark
    : clientThemePalette[CLIENT_THEME].light;
  const fontColor = palette.textPrimary;
  const navigate = useNavigate();

  const handleClick = (e: any) => {
    e.preventDefault();
    navigate('/tire-analytics/notification', {
      state: {
        data: {},
      },
    });
  };

  const chartColors = [
    '#EB5757',
    palette.primary,
    '#00e396',
    '#feb019',
    '#775dd0',
    '#f46036',
  ];
  const onPieClick = (data: any, index: number) => {
    setActiveIndices((prevState) => ({
      ...prevState,
      [index]: !prevState[index],
    }));
  };

  const processData = (rawData: any[]) => {
    const grouped: Record<
      string,
      { name: string; deviceId: string; tireName: string; count: number }
    > = {};

    rawData.forEach((item) => {
      const deviceId = item.asset.bumperNumber;
      const assetTireDetails = getDynamicAssetTireInfo(
        makeModelDetails,
        tirePatterns.tirePatternPositions,
        item.asset.make,
        item.asset.model,
        customerId
      );
      const tirePositions = assetTireDetails.tirePositions;
      const tireName = getTireName(
        item.rule?.ruleConditionAssociations[0]?.tagName ?? 'N/A',
        tirePositions
      );

      const key = `${deviceId} - ${tireName}`;

      if (grouped[key]) {
        grouped[key].count += 1;
      } else {
        grouped[key] = {
          name: key,
          deviceId,
          tireName,
          count: 1,
        };
      }
    });

    return Object.values(grouped)
      .sort((a, b) => b.count - a.count)
      .slice(0, 5);
  };

  const filteredData = filterLastDaysActive(data);
  const processedData = processData(filteredData);

  return (
    <Box
      sx={{
        border: '1px solid #2F445E',
        borderRadius: '4px',
        backgroundColor: palette.paperBackground,
        padding: '10px',
      }}
    >
      <Typography
        sx={{
          fontSize: '14px',
          fontWeight: '600',
          color: ThemePalette.typography.grey,
        }}
      >
        {t('tire_analytics')}
      </Typography>

      {processedData.length > 0 && (
        <Box
          sx={{
            marginBottom: '10px',
          }}
        >
          <div
            style={{ width: '100%', height: '340px' }}
            onFocus={handleFocusRemoveSelectOutline}
          >
            <ResponsiveContainer width="100%" height="100%">
              <PieChart margin={{ top: 30, right: 90, left: 40, bottom: 0 }}>
                <Pie
                  data={processedData}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  outerRadius={120}
                  paddingAngle={2}
                  fill="#8884d8"
                  dataKey="count"
                  activeIndex={Object.keys(activeIndices)
                    .map(Number)
                    .filter((i) => activeIndices[i])}
                  activeShape={renderActiveShape}
                  onClick={onPieClick}
                >
                  {processedData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={chartColors[index]} />
                  ))}
                </Pie>

                <Legend
                  verticalAlign="bottom"
                  formatter={(value, entry) => (
                    <span style={{ color: entry.color, fontSize: '12px' }}>
                      {value}
                    </span>
                  )}
                />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </Box>
      )}

      <Box
        sx={{
          marginTop: '10px',
          display: 'flex',
          alignItems: 'center',
        }}
      >
        {processedData.length > 0 ? (
          <>
            <Typography
              sx={{
                color: fontColor,
                fontSize: '12px',
              }}
            >
              {t('top_asset_tires_with_most_notifications')}
            </Typography>

            <Button
              sx={{
                marginLeft: '5px',
                textTransform: 'none',
                border: '1px solid #2F445E',
                borderRadius: '4px',
                fontSize: '12px',
                color: ThemePalette.typography.white,
              }}
              onClick={handleClick}
            >
              {t('click_to_view_more')}
            </Button>
          </>
        ) : (
          <>
            <Typography
              sx={{
                color: fontColor,
                fontSize: '12px',
              }}
            >
              {t('no_tpms_notification')}
            </Typography>
          </>
        )}
      </Box>
    </Box>
  );
}

const renderActiveShape = (props: any) => {
  const RADIAN = Math.PI / 180;
  const {
    cx,
    cy,
    midAngle,
    innerRadius,
    outerRadius,
    startAngle,
    endAngle,
    fill,
    value,
  } = props;
  const sin = Math.sin(-RADIAN * midAngle);
  const cos = Math.cos(-RADIAN * midAngle);
  const sx = Number(cx) + (Number(outerRadius) + 10) * cos;
  const sy = Number(cy) + (Number(outerRadius) + 10) * sin;
  const mx = Number(cx) + (Number(outerRadius) + 30) * cos;
  const my = Number(cy) + (Number(outerRadius) + 30) * sin;
  const ex = mx + (cos >= 0 ? 1 : -1) * 22;
  const ey = my;
  const textAnchor = cos >= 0 ? 'start' : 'end';
  return (
    <g>
      <Sector
        cx={cx}
        cy={cy}
        innerRadius={innerRadius}
        outerRadius={outerRadius}
        startAngle={startAngle}
        endAngle={endAngle}
        fill={fill}
      />
      <Sector
        cx={cx}
        cy={cy}
        startAngle={startAngle}
        endAngle={endAngle}
        innerRadius={Number(outerRadius) + 6}
        outerRadius={Number(outerRadius) + 10}
        fill={fill}
      />
      <path
        d={`M${sx},${sy}L${mx},${my}L${ex},${ey}`}
        stroke={fill}
        fill="none"
      />
      <circle cx={ex} cy={ey} r={2} fill={fill} stroke="none" />
      <text
        x={Number(ex) + (cos >= 0 ? 1 : -1) * 12}
        y={ey}
        textAnchor={textAnchor}
        fill={fill}
        fontSize={10}
      >{`${value.toFixed(0)} Notifications`}</text>
    </g>
  );
};
